import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { gsap, ScrollTrigger, useGSAP } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { SplitChars } from '@/components/motion/split-text'
import { timeline } from '@/constants/content'

/** Seven years in rows; each year lights up as it crosses the middle of the screen. */
const Timeline: FC = (): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useGSAP(() => {
        if (!root.current || prefersReducedMotion()) return

        const rows = gsap.utils.toArray<HTMLElement>('[data-year-row]', root.current)

        rows.forEach(row => {
            const year = row.querySelector('[data-year]')

            gsap.set(year, { opacity: 0.22 })

            ScrollTrigger.create({
                trigger: row,
                start: 'top 62%',
                end: 'bottom 38%',
                onToggle: self => {
                    gsap.to(year, { opacity: self.isActive ? 1 : 0.22, duration: 0.6, ease: 'power2.out' })
                    row.classList.toggle('is-active', self.isActive)
                }
            })
        })
    }, { scope: root })

    return (
        <section ref={root} aria-labelledby='timeline-title' className='border-t border-line py-24 gutter md:py-36'>
            <div className='grid gap-12 md:grid-cols-12 md:gap-8'>
                <div className='md:col-span-5'>
                    <div className='md:sticky md:top-[calc(var(--header-height)+40px)]'>
                        <p data-reveal='fade' className='label-mono text-acid'>
                            (02) — Timeline
                        </p>

                        <h2 id='timeline-title' data-reveal='chars' className='mt-6 display-xl uppercase'>
                            <span className='block'>
                                <SplitChars text='Seven years,' />
                            </span>

                            <span className='block'>
                                <SplitChars text='one wardrobe' />
                            </span>
                        </h2>
                    </div>
                </div>

                <ol className='md:col-span-7'>
                    {timeline.map((entry, index) => {
                        const last = index === timeline.length - 1

                        return (
                            <li
                                key={entry.year}
                                data-year-row
                                data-reveal='up'
                                className='group grid grid-cols-[auto_1fr] items-start gap-x-6 gap-y-2 border-b border-line py-7 first:border-t md:gap-x-10 md:py-9'
                            >
                                <span data-year className={cn('font-heading text-[clamp(44px,5vw,80px)] leading-[0.85]', last ? 'text-acid' : 'text-bone')}>
                                    {entry.year}
                                </span>

                                <span className='pt-1'>
                                    <span className='block title-sm text-bone'>
                                        {entry.title}
                                    </span>

                                    <span className='mt-2 block max-w-[46ch] body-copy text-bone/60'>
                                        {entry.detail}
                                    </span>
                                </span>
                            </li>
                        )
                    })}
                </ol>
            </div>
        </section>
    )
}

export default Timeline
